/**
 * The calls the panel and the detail view make. The image PUT deliberately bypasses `apiClient`: a presigned URL
 * binds only the content-type header, and the client's Authorization and X-Site-Id headers would
 * break the signature (and leak the bearer token to S3).
 */
import apiClient from '@/api/client';
import type {
  AvailabilityResponse,
  ConfirmRequest,
  ConfirmResponse,
  SaveRequest,
  SaveResponse,
} from './types';
import type { FeedbackStatus } from './feedbackDetailTypes';

export async function fetchAvailability(): Promise<AvailabilityResponse> {
  const response = await apiClient.get<AvailabilityResponse>('/api/feedback/availability');
  return response.data;
}

export async function saveFeedback(body: SaveRequest, idempotencyKey: string): Promise<SaveResponse> {
  const response = await apiClient.post<SaveResponse>('/api/feedback/save', body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return response.data;
}

/** The `POST /api/feedback/status` response. */
export interface StatusUpdate {
  feedbackId: string;
  status: FeedbackStatus;
  statusUpdatedAt: string;
  statusUpdatedBy: string;
}

/** `POST /api/feedback/status` (IC-950): any View My Feedback holder sets one record's status. */
export async function updateFeedbackStatus(
  feedbackId: string,
  status: FeedbackStatus,
  idempotencyKey: string,
): Promise<StatusUpdate> {
  const response = await apiClient.post<StatusUpdate>(
    '/api/feedback/status',
    { feedbackId, status },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return response.data;
}

export async function confirmAttachments(body: ConfirmRequest): Promise<ConfirmResponse> {
  const response = await apiClient.post<ConfirmResponse>('/api/feedback/attachments/confirm', body);
  return response.data;
}

/** Uploads one image to its presigned URL. Resolves true on a 2xx, false on any failure. */
export async function putImage(url: string, headers: Record<string, string>, blob: Blob): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'PUT', headers, body: blob });
    return response.ok;
  } catch {
    return false;
  }
}
